import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ResourcePage from "@/components/ResourcePage";
import { formatDeDate } from "@/lib/dateFormat";

const AMPEL_DOT = {
  gruen: "bg-emerald-500",
  gelb: "bg-amber-400",
  rot: "bg-rose-500",
};
const AMPEL_LABEL = { gruen: "Grün", gelb: "Gelb", rot: "Rot" };

function AmpelPill({ status }) {
  const s = status || "gruen";
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700"
      data-testid={`contacts-ampel-${s}`}
    >
      <span className={`inline-block w-2.5 h-2.5 rounded-full ${AMPEL_DOT[s] || AMPEL_DOT.gruen}`} />
      {AMPEL_LABEL[s] || s}
    </span>
  );
}

export default function ContactsPage() {
  const navigate = useNavigate();
  const [includeRed, setIncludeRed] = useState(false);

  return (
    <ResourcePage
      resource="contacts"
      title="Personen"
      subtitle="Alle Menschen, mit denen du zu tun hast – Ampel, Anker, letzter Kontakt."
      singular="Person"
      newButtonLabel="Person anlegen"
      createDialogTitle="Person anlegen"
      editDialogTitle="Person bearbeiten"
      submitButtonLabel="Person speichern"
      emptyDescription="Lege deine erste Person an."
      emptyCreateButtonLabel="Person anlegen"
      testidPrefix="contacts"
      onRowClick={(row) => navigate(`/contacts/${row.id}`)}
      extraParams={{ include_red: includeRed ? "true" : "false" }}
      extraToolbar={
        <label
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gray-200 bg-white text-xs text-gray-700 cursor-pointer hover:bg-gray-50"
          data-testid="contacts-toggle-red-wrap"
        >
          <input
            type="checkbox"
            checked={includeRed}
            onChange={(e) => setIncludeRed(e.target.checked)}
            className="accent-black"
            data-testid="contacts-toggle-red"
          />
          Rot ausgeblendete zeigen
        </label>
      }
      fields={[
        {
          name: "salutation",
          label: "Anrede",
          type: "select",
          placeholder: "— Keine —",
          allowNone: { label: "— Keine —" },
          options: [
            { value: "Herr", label: "Herr" },
            { value: "Frau", label: "Frau" },
            { value: "Divers", label: "Divers" },
          ],
        },
        { name: "title", label: "Titel", type: "text", placeholder: "z. B. Dr., Prof. Dr., Dipl.-Ing." },
        { name: "first_name", label: "Vorname", type: "text", required: true },
        { name: "last_name", label: "Nachname", type: "text", required: true },
        { name: "email", label: "E-Mail", type: "email" },
        { name: "phone", label: "Telefon", type: "tel" },
        { name: "mobile", label: "Mobil", type: "tel" },
        { name: "position", label: "Position", type: "text" },
        {
          name: "company_id",
          label: "Firma",
          type: "select",
          optionsFromResource: "companies",
          placeholder: "Firma wählen…",
          allowNone: { label: "— Keine Firma zuordnen —" },
          withInlineCreate: {
            resource: "companies",
            singular: "Firma",
            buttonLabel: "Firma anlegen",
            fields: [
              { name: "name", label: "Firmenname", type: "text", required: true },
              { name: "industry", label: "Branche", type: "text" },
              { name: "email", label: "E-Mail", type: "email" },
              { name: "phone", label: "Telefon", type: "tel" },
              { name: "website", label: "Website", type: "text", placeholder: "https://…" },
              { name: "street", label: "Straße", type: "text" },
              { name: "house_number", label: "Hausnr.", type: "text" },
              { name: "postal_code", label: "PLZ", type: "text" },
              { name: "city", label: "Ort", type: "text" },
              { name: "country", label: "Land", type: "text" },
            ],
          },
        },
        {
          name: "ampel_status",
          label: "Ampelstatus",
          type: "select",
          options: [
            { value: "gruen", label: "Grün" },
            { value: "gelb", label: "Gelb" },
            { value: "rot", label: "Rot" },
          ],
          placeholder: "Ampelstatus wählen",
        },
        { name: "ampel_datum", label: "Ampel: Entscheidungsdatum", type: "date" },
        { name: "ampel_grund", label: "Ampel: Grund", type: "textarea", rows: 2, fullWidth: true },
        {
          name: "anker_kennengelernt",
          label: "Wie kennengelernt?",
          type: "textarea",
          rows: 2,
          fullWidth: true,
          placeholder: "z.B. Auf welcher Konferenz, wer hat vermittelt…",
        },
        {
          name: "anker_gemeinsamer_moment",
          label: "Gemeinsamer Moment / Anekdote",
          type: "textarea",
          rows: 2,
          fullWidth: true,
        },
        {
          name: "anker_persoenliche_verbindung",
          label: "Persönliche Verbindung / Interessen / Hobbys",
          type: "textarea",
          rows: 2,
          fullWidth: true,
        },
        { name: "naechster_schritt", label: "Nächster Schritt / offene Zusage", type: "textarea", rows: 2, fullWidth: true },
        { name: "naechster_termin", label: "Nächster Termin / Wiedervorlage", type: "date" },
        { name: "notes", label: "Freie Notizen", type: "textarea", fullWidth: true },
      ]}
      columns={[
        {
          key: "ampel",
          label: "Ampel",
          render: (r) => <AmpelPill status={r.ampel_status} />,
        },
        {
          key: "name",
          label: "Name",
          render: (r) => (
            <span className="font-medium text-gray-900">
              {r.first_name} {r.last_name}
            </span>
          ),
        },
        { key: "position", label: "Position" },
        { key: "company_name", label: "Firma" },
        {
          key: "letzter_kontakt_datum",
          label: "Letzter Kontakt",
          render: (r) =>
            r.letzter_kontakt_datum ? (
              <span className="text-xs text-gray-600">
                {formatDeDate(r.letzter_kontakt_datum)}
                {r.letzter_kontakt_art ? ` · ${r.letzter_kontakt_art}` : ""}
              </span>
            ) : (
              <span className="text-gray-400 text-xs">—</span>
            ),
        },
        {
          key: "naechster_termin",
          label: "Nächster Termin",
          render: (r) =>
            r.naechster_termin ? (
              <span className="text-xs text-gray-600">
                {formatDeDate(r.naechster_termin)}
              </span>
            ) : (
              <span className="text-gray-400 text-xs">—</span>
            ),
        },
      ]}
    />
  );
}
